import { useCallback, useMemo } from 'react'
import { Skeleton, Stack, Text, Title } from '@mantine/core'
import { SearchBar } from './SearchBar'
import { ClaimsList } from '../ClaimsList'
import { Button } from '../../Common/Button'
import { StatusMessage } from '../../Common/StatusMessage'
import { useClaimSearch } from '../../../hooks/useClaimSearch'
import type { SearchOptions } from '../../../types/search'

const MAX_RECENT_CLAIMS = 10

const SEARCH_OPTIONS: SearchOptions[] = [
  { value: 'reference', label: 'By Reference' },
  { value: 'insuredName', label: 'By Insured Name' },
  { value: 'policyNumber', label: 'By Policy Number' },
  { value: 'customerReference', label: 'By Customer Reference' },
  { value: 'dateOfLoss', label: 'By Date of Loss' },
  { value: 'client', label: 'By Client' },
  { value: 'broker', label: 'By Broker' },
  { value: 'filename', label: 'By Filename' },
]

export interface ClaimSearchPageProps {
  onConnect: (claim: { claimNumber: string } | null) => void
  highlightClaimNumber?: string
  detectionMessage?: string
  connectedClaimNumber?: string
}

export function ClaimSearchPage({
  onConnect,
  highlightClaimNumber,
  detectionMessage,
  connectedClaimNumber,
}: Readonly<ClaimSearchPageProps>) {
  const {
    query,
    searchType,
    results,
    recent,
    loading,
    recentLoading,
    recentError,
    error,
    hasSearched,
    setQuery,
    setSearchType,
    search,
    clearSearch,
    loadRecentClaims,
  } = useClaimSearch()
  const displayedRecent = useMemo(() => recent.slice(0, MAX_RECENT_CLAIMS), [recent])

  const handleConnect = useCallback(
    (claimNumber: string) => {
      if (connectedClaimNumber === claimNumber) {
        onConnect(null)
      } else {
        onConnect({ claimNumber })
      }
    },
    [connectedClaimNumber, onConnect]
  )

  const claimCardSkeleton = (
    <Stack gap="xs">
      {[1, 2, 3].map((i) => (
        <Skeleton key={i} height={80} />
      ))}
    </Stack>
  )

  const recentClaimsSection = (
    <Stack gap="md">
      {detectionMessage && (
        <Text fz="sm" c="dimmed" role="alert">
          {detectionMessage}
        </Text>
      )}
      <Title order={5}>My Recent Claims ({displayedRecent.length})</Title>
      {recentLoading && claimCardSkeleton}
      {!recentLoading && recentError && (
        <Stack gap="xs">
          {/* role="status", not the default alert: the search-error branch renders this
              section too, so two alerts would land in the one aria-live wrapper below. */}
          <StatusMessage status="error" role="status">
            {recentError}
          </StatusMessage>
          <Button label="Retry" size="xs" variant="outline" onClick={loadRecentClaims} />
        </Stack>
      )}
      {!recentLoading && !recentError && displayedRecent.length === 0 && (
        <Text fz="sm" c="dimmed">
          No recent claims. Use search to find claims.
        </Text>
      )}
      {!recentLoading && !recentError && displayedRecent.length > 0 && (
        <ClaimsList
          claims={displayedRecent}
          onConnect={handleConnect}
          highlightClaimNumber={highlightClaimNumber}
          connectedClaimNumber={connectedClaimNumber}
        />
      )}
    </Stack>
  )

  return (
    <Stack gap="md" p="md" mih="100vh">
      <Title order={4}>Search Claims</Title>
      <SearchBar
        searchType={searchType}
        searchValue={query}
        searchOptions={SEARCH_OPTIONS}
        onSearchTypeChange={(val) => {
          setSearchType(val || 'reference')
          clearSearch()
        }}
        onSearchValueChange={setQuery}
        onSearch={search}
        onClear={clearSearch}
      />
      <div
        aria-live="polite"
        aria-atomic="true"
        style={{
          backgroundColor: 'var(--section-bg)',
          margin: 'calc(-1 * var(--mantine-spacing-md))',
          marginTop: 0,
          padding: 'var(--mantine-spacing-md)',
        }}
      >
        {!hasSearched && recentClaimsSection}
        {hasSearched && (
          <Stack gap="md">
            {loading && claimCardSkeleton}
            {!loading && error && (
              <>
                <Stack gap="xs">
                  <StatusMessage status="error">{error}</StatusMessage>
                  <Button label="Retry" size="xs" variant="outline" onClick={search} />
                </Stack>
                {recentClaimsSection}
              </>
            )}
            {!loading && !error && (
              <>
                <Title order={5}>Search Results ({results.length})</Title>
                <Text fz="xs" c="dimmed">
                  Search results for &apos;{query}&apos;
                </Text>
                <ClaimsList claims={results} onConnect={handleConnect} connectedClaimNumber={connectedClaimNumber} />
              </>
            )}
          </Stack>
        )}
      </div>
    </Stack>
  )
}
